<script lang="ts">
    /**
     * Cloudflare Turnstile CAPTCHA Component
     * Usage: <Turnstile bind:token={token} bind:this={ref} />
     * ref.reset() can be called to reset the widget
     */
    import { PUBLIC_TURNSTILE_SITE_KEY } from '$env/static/public';
    import { onMount } from 'svelte';

    interface Props {
        token?: string;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact' | 'flexible';
        onError?: (error: string) => void;
    }

    let { token = $bindable(), theme = 'dark', size = 'flexible', onError }: Props = $props();

    let container: HTMLDivElement;
    let widgetId: string | undefined;
    let scriptElement: HTMLScriptElement | null = null;
    let callbackName: string;
    let isLoading = $state(true);
    let hasError = $state(false);
    let retryCount = $state(0);
    const MAX_RETRIES = 3;

    // Extract nonce from existing script tags for CSP compliance
    // Improved: Look for specific data attribute first, then fallback to inline scripts
    function getCspNonce(): string | undefined {
        // Look for a specific script with data-nonce-source attribute
        const nonceScript = document.querySelector('script[data-nonce-source="turnstile"]');
        if (nonceScript) {
            return nonceScript.getAttribute('nonce') || undefined;
        }

        // Fallback: look for nonce in inline scripts (prefer over external scripts)
        const scripts = document.querySelectorAll('script[nonce]');
        for (const script of scripts) {
            const htmlScript = script as HTMLScriptElement;
            if (!htmlScript.src) { // Prefer inline scripts
                const scriptNonce = script.getAttribute('nonce');
                if (scriptNonce) return scriptNonce;
            }
        }

        return undefined;
    }

    onMount(() => {
        // Generate unique callback name to avoid collisions
        callbackName = `onTurnstileLoad_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

        if ((window as any).turnstile) {
            renderWidget();
        } else {
            loadScript();
        }

        return () => {
            removeWidget();
            // Clean up script element
            if (scriptElement && scriptElement.parentNode) {
                scriptElement.remove();
            }
            // Clean up global callback
            if (callbackName && (window as any)[callbackName]) {
                delete (window as any)[callbackName];
            }
        };
    });

    function loadScript() {
        const cspNonce = getCspNonce();
        scriptElement = document.createElement('script');
        scriptElement.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=${callbackName}`;
        scriptElement.async = true;
        scriptElement.defer = true;
        scriptElement.crossOrigin = 'anonymous';
        if (cspNonce) scriptElement.nonce = cspNonce;

        // Define unique global callback
        (window as any)[callbackName] = () => {
            renderWidget();
        };

        scriptElement.onerror = () => {
            console.error(`Failed to load Turnstile script (attempt ${retryCount + 1}/${MAX_RETRIES})`);

            if (retryCount < MAX_RETRIES - 1) {
                retryCount++;
                // Exponential backoff: 1s, 2s, 4s
                const delayMs = 1000 * Math.pow(2, retryCount - 1);
                setTimeout(loadScript, delayMs);
            } else {
                isLoading = false;
                hasError = true;
                onError?.('Failed to load security verification after multiple attempts');
            }
        };

        document.head.appendChild(scriptElement);
    }

    function renderWidget() {
        if (!container || !(window as any).turnstile) return;

        removeWidget();

        const turnstile = (window as any).turnstile;
        try {
            widgetId = turnstile.render(container, {
                sitekey: PUBLIC_TURNSTILE_SITE_KEY,
                theme: theme,
                size: size,
                callback: (response: string) => {
                    token = response;
                    isLoading = false;
                },
                'expired-callback': () => {
                    token = '';
                },
                'error-callback': () => {
                    token = '';
                    isLoading = false;
                    hasError = true;
                    onError?.('Security verification expired or failed');
                }
            });
            // Widget rendered successfully
            isLoading = false;
        } catch (error) {
            console.error('Turnstile render error:', error);
            isLoading = false;
            hasError = true;
            onError?.('Failed to render security verification');
        }
    }

    function removeWidget() {
        if (widgetId && (window as any).turnstile) {
            try {
                (window as any).turnstile.remove(widgetId);
            } catch (e) {
                // Widget may already be removed
            }
            widgetId = undefined;
        }
    }

    // Reset the widget - can be called externally via bind:this
    function reset() {
        if (widgetId && (window as any).turnstile) {
            try {
                (window as any).turnstile.reset(widgetId);
                token = '';
            } catch (e) {
                // If reset fails, try re-rendering
                renderWidget();
            }
        }
    }

    // Expose reset method to parent
    export { reset };
</script>

<div class="turnstile-container" aria-label="Security verification" role="region">
    <!-- Loading state -->
    {#if isLoading}
        <div class="flex items-center justify-center gap-2 py-4 text-ak-text-muted">
            <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span class="text-sm">Loading security verification...</span>
        </div>
    {/if}

    <!-- Error state -->
    {#if hasError}
        <div class="flex items-center justify-center gap-2 py-4 text-ak-error">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span class="text-sm">Security verification failed. Please refresh.</span>
        </div>
    {/if}

    <!-- Turnstile widget container -->
    <div bind:this={container} class="flex justify-center" class:hidden={isLoading || hasError} data-size={size}></div>

    <!-- Configuration warning -->
    {#if !PUBLIC_TURNSTILE_SITE_KEY}
        <p class="text-xs text-amber-400 text-center">Turnstile not configured - missing PUBLIC_TURNSTILE_SITE_KEY</p>
    {/if}
</div>

<style>
    .turnstile-container {
        min-height: 65px; /* Reserve space to prevent layout shift */
    }

    /* Adaptive height based on size prop - :global wrapper for dynamic attributes */
    /* compact is the only variant taller than 65px (Cloudflare renders it ~120px) */
    :global(.turnstile-container[data-size="compact"]) {
        min-height: 120px;
    }

    .turnstile-container :global(.hidden) {
        display: none;
    }

    @media (prefers-reduced-motion: reduce) {
        :global(.animate-spin) {
            animation: none;
        }
    }
</style>
